import { ReactNode } from 'react';

import { DonutSlice, LegendItem } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { toSigDigits, toUsFormat } from 'utils/utils';
import {
	buildFillRingData,
	buildSegmentedFillRingData,
	formatCompactRevenue,
	formatFillCenterLabel,
	emptyRing,
	lightenHex,
	expiredShiftHex,
	orderFillLegend,
	paidCount,
	revenueTooltipFormatter,
	COLOR_AVAIL,
	COLOR_POOL_BLOCKED,
	LIGHTEN_FACTOR,
} from './inventoryUtils';
import type { InventoryColumn } from './useTicketInventoryData';
import type { InventoryColumnCardProps } from './InventoryColumnCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ColumnPalette {
	ticketTypeColors: Record<string, string>;
	poolColors: Record<string, string>;
	onHoldColor: string;
	compedColor: string;
}

// ---------------------------------------------------------------------------
// Module-level formatters (stable references — no need to recreate per column)
// ---------------------------------------------------------------------------

const freeTooltipFormatter = (_value: number, name: string): [string, string] => ['$0.00', name];

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export function buildTypeColumnProps(
	col: InventoryColumn,
	palette: ColumnPalette,
	t: (key: string) => string
): InventoryColumnCardProps {
	const { ticketTypeColors, onHoldColor, compedColor } = palette;

	const typeColor = ticketTypeColors[col.name] ?? COLOR_AVAIL;
	const availColor = lightenHex(typeColor, LIGHTEN_FACTOR);
	const expiredColor = expiredShiftHex(typeColor);
	const isFree = !col.payWhatYouWant && col.initialPrice === 0;
	const isPWYW = col.payWhatYouWant;
	const isPriced = !col.payWhatYouWant && col.initialPrice > 0;
	// Revenue (and the issued donut's Sold slice) is paid-only — comped seats went
	// out at $0 and never capture revenue.
	const paid = paidCount(col.issued, col.compedCount);

	// Reserve = genuine per-type holds (comp/keep). NOT a pool.
	const reserveAmount = col.hold?.amount ?? 0;
	// A pooled tier's `available` is pool-capped (min of its own and the shared remaining). The part the
	// shared pool blocks — the tier's own leftover seats it can't sell — renders as a neutral "Pool full"
	// slice (NOT a member-coloured wedge, bug 2c), so the ring still sums to trueCapacity while "Available"
	// honestly reads the pool-capped sellable count (0 when the pool is full). docs/pools_business_logic.md §3.
	// Sourced from the shared useEventCapacity hook (col.poolBlocked) so it can't drift from `available`;
	// standalone tiers have poolBlocked 0 (no Pool-full slice).
	const poolLimited = col.poolBlocked;
	const availLabel = t('analytics-inventory-available');
	const poolFullLabel = t('analytics-inventory-pool-full');

	// ── Tile ──────────────────────────────────────────────────────────────────

	const maxPriceTile = isPWYW && col.trueCapacity > 0 ? col.ceiling / col.trueCapacity : 0;
	const midpointEstTile = isPWYW ? (paid * (col.initialPrice + maxPriceTile)) / 2 : 0;
	const tileValue = isFree ? 'Free' : isPWYW ? `${toSigDigits(midpointEstTile)}` : toSigDigits(col.payout);

	const tileHelpButton: ReactNode = (
		<PunchupHelpButton
			mainText={
				isPWYW
					? `Estimated midpoint revenue for "${col.name}" tickets — actual amount depends on what buyers chose to pay. Comped tickets are excluded (they capture no revenue).`
					: `Price-based revenue estimate for "${col.name}" — paid tickets × unit price. Comped tickets are excluded (they capture no revenue).`
			}
			codeText={
				isPWYW
					? 'estimate = paid × (floor + ceiling) / 2  (comps excluded)\n* = midpoint estimate, not a confirmed amount'
					: 'revenue = paid × unit price  (comps excluded)'
			}
		/>
	);

	// ── Issued donut ──────────────────────────────────────────────────────────

	const soldLabel = isFree ? t('analytics-inventory-claimed') : t('analytics-inventory-sold');
	const reserveLabel = t('analytics-inventory-on-hold');

	const issuedData: DonutSlice[] = [
		paid > 0 ? { label: soldLabel, value: paid, color: typeColor } : null,
		col.compedCount > 0 ? { label: t('analytics-inventory-comped'), value: col.compedCount, color: compedColor } : null,
		reserveAmount > 0 ? { label: reserveLabel, value: reserveAmount, color: onHoldColor } : null,
		col.available > 0 ? { label: availLabel, value: col.available, color: availColor } : null,
		poolLimited > 0 ? { label: poolFullLabel, value: poolLimited, color: COLOR_POOL_BLOCKED } : null,
		col.expired > 0 ? { label: 'Expired', value: col.expired, color: expiredColor } : null,
	].filter(Boolean) as DonutSlice[];

	const issuedLegendItems: LegendItem[] = [
		{ label: soldLabel, color: typeColor },
		...(col.compedCount > 0 ? [{ label: t('analytics-inventory-comped'), color: compedColor }] : []),
		...(reserveAmount > 0 ? [{ label: reserveLabel, color: onHoldColor }] : []),
		...(col.available > 0 ? [{ label: availLabel, color: availColor }] : []),
		...(poolLimited > 0 ? [{ label: poolFullLabel, color: COLOR_POOL_BLOCKED }] : []),
		...(col.expired > 0 ? [{ label: 'Expired', color: expiredColor }] : []),
	];

	const issuedHelpButton: ReactNode = (
		<PunchupHelpButton
			mainText={`How capacity for "${col.name}" is allocated.`}
			codeText={[
				isFree ? 'Claimed = all tickets taken' : 'Sold = paid tickets (comps counted separately below)',
				...(reserveAmount > 0 ? ['On Hold = capacity set aside for this tier'] : []),
				'Available = open inventory (lightened)',
				'Expired = unsold past on-sale window',
			].join('\n')}
			secondaryText={
				reserveAmount > 0
					? 'Releasing a hold returns those reserved tickets to public sale immediately.'
					: 'Once issued tickets reach capacity, no further sales are possible.'
			}
		/>
	);

	// ── Fill ring ─────────────────────────────────────────────────────────────

	const pwywEffectivePrice = isPWYW && col.trueCapacity > 0 ? col.ceiling / col.trueCapacity : 0;
	const midpointPrice = (col.initialPrice + pwywEffectivePrice) / 2;
	const midpointEstimate = paid * midpointPrice;
	const isSegmented = isPriced || (isPWYW && col.ceiling > 0);
	const overflow = col.payout > 0 && col.ceiling === 0;
	// Pool-capped revenue: "Available" revenue is the pool-capped sellable remaining (col.available), and the
	// pool-blocked seats render as a neutral "Pool full" slice — their revenue can't be earned. A full pool →
	// no available revenue → the ring reads as fully captured, matching the issued donut. §3 of the pool doc.
	const fillCol = { ...col, poolBlocked: poolLimited };

	const fillData: DonutSlice[] = isFree
		? col.issued > 0
			? [{ label: t('analytics-inventory-claimed'), value: col.issued, color: typeColor }]
			: emptyRing()
		: isSegmented
		? buildSegmentedFillRingData(
				isPriced ? fillCol : { ...fillCol, initialPrice: midpointPrice },
				typeColor,
				onHoldColor,
				compedColor
		  )
		: buildFillRingData(col.payout, col.ceiling, typeColor);

	// Fill legend ordering is centralised in orderFillLegend (single source) — "Available" follows "Sold",
	// then the secondary states — so this and the summary fill legend can never drift out of order.
	const fillLegendItems: LegendItem[] | undefined = isFree
		? [{ label: t('analytics-inventory-claimed'), color: typeColor }]
		: isSegmented
		? orderFillLegend([
				{ key: 'sold', label: 'Sold', color: typeColor },
				...(col.available > 0 ? [{ key: 'available' as const, label: 'Available', color: availColor }] : []),
				...(poolLimited > 0 ? [{ key: 'poolFull' as const, label: poolFullLabel, color: COLOR_POOL_BLOCKED }] : []),
				...(col.compedCount > 0
					? [{ key: 'comped' as const, label: t('analytics-inventory-comped'), color: compedColor }]
					: []),
				...(reserveAmount > 0 ? [{ key: 'onHold' as const, label: reserveLabel, color: onHoldColor }] : []),
				...(col.expired > 0 ? [{ key: 'expired' as const, label: 'Expired', color: expiredColor }] : []),
		  ])
		: undefined;

	const pwywMinEst = paid * col.initialPrice;
	const fillCenterLabel = isPWYW
		? `${formatCompactRevenue(midpointEstimate)}`
		: isFree
		? '$0.00'
		: formatFillCenterLabel(col);

	const fillCenterTooltip = isPWYW
		? `Estimated midpoint: ${toUsFormat(midpointEstimate)} — range ${toUsFormat(pwywMinEst)}–${toUsFormat(col.payout)}`
		: isFree
		? `${col.issued} tickets claimed — free tickets contribute $0.00 revenue`
		: col.ceiling > 0
		? `${toUsFormat(col.payout)} of ${toUsFormat(col.ceiling)} revenue ceiling`
		: undefined;

	const fillHelpButton: ReactNode =
		col.ceiling <= 0 && !isFree ? (
			overflow ? (
				<PunchupHelpButton
					mainText={`Revenue fill for "${col.name}" is in overflow — no fixed ceiling, so any payout pushes fill above 100%. Center shows the price-based estimate.`}
					codeText={'PWYW: fill > 100% (no fixed ceiling)\ncenter = price-based total (compact)'}
					secondaryText="Overflow confirms buyers paid above the floor price."
				/>
			) : (
				<PunchupHelpButton
					mainText={`"${col.name}" has no fixed price — no revenue ceiling applies.`}
					codeText="PWYW: no ceiling defined"
					secondaryText="A Pay what you want ring enters overflow as soon as the first payment arrives."
				/>
			)
		) : isFree ? (
			<PunchupHelpButton
				mainText={`"${col.name}" is a free ticket — the solid ring shows all claimed tickets. Center shows $0.00 because free tickets generate no revenue.`}
				codeText={'Claimed = all tickets taken (fills the full ring)\n$0.00 = no revenue ceiling for free types'}
				secondaryText="Free tickets count toward attendance but contribute no revenue."
			/>
		) : (
			<PunchupHelpButton
				mainText={`How much of the revenue ceiling for "${
					col.name
				}" has been captured — segments show sold (paid), comped, held, available${
					poolLimited > 0 ? ', pool-blocked' : ''
				}, and expired capacity valued at the unit price. Comped seats capture no revenue, so they sit outside the paid arc.`}
				codeText={[
					'Sold = paid × price  (type colour)',
					'Comped = comped × price  (given away, $0 captured)',
					'Available = sellable × price  (lightened)',
					...(poolLimited > 0 ? ['Pool full = blocked by shared pool × price  (grey)'] : []),
					'Expired = expired × price  (grey)',
					'center = paid revenue (compact)',
				].join('\n')}
				secondaryText={
					poolLimited > 0
						? 'A shared pool caps how many of this tier can still sell — the grey "Pool full" slice is revenue the pool blocks.'
						: "Revenue potential is the tier's own price × its full capacity."
				}
			/>
		);

	return {
		tile: {
			title: col.name,
			value: tileValue,
			descriptions: [col.description],
			helpButton: tileHelpButton,
		},
		issuedDonut: {
			centerLabel: `${col.issued}/${col.trueCapacity}`,
			centerTooltip: `${col.issued} ${isFree ? 'claimed' : 'issued (sold + comped)'} of ${
				col.trueCapacity
			} total capacity`,
			data: issuedData.length > 0 ? issuedData : emptyRing(),
			legendItems: issuedLegendItems,
			helpButton: issuedHelpButton,
		},
		fillDonut: {
			centerLabel: fillCenterLabel,
			centerTooltip: fillCenterTooltip,
			data: fillData,
			legendItems: fillLegendItems,
			tooltipFormatter: isFree ? freeTooltipFormatter : isSegmented ? revenueTooltipFormatter : undefined,
			helpButton: fillHelpButton,
		},
	};
}
